import { Canvas, useFrame } from '@react-three/fiber';
import { cn } from '@sohanemon/utils';
import { useWebGLAvailable } from '@sohanscript/lib/hooks/webgl';
import { isLightTheme } from '@sohanscript/theme';
import { useResolvedTheme } from '@sohanscript/theme/store';
import type * as React from 'react';
import { Component, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { getGlassSphereColors } from './theme-colors';

/* ── Design system palette ─────────────────────────────────────────────────────
 *   primary:  #6fcbff  (bright cyan)
 *   accent:   #3a8b9f  (teal)
 *   light:    #a4c9df  (light blue)
 *   muted:    #5f96ac  (muted teal)
 *   surface:  #0a2232  (background-light)
 *   bg:       #071828  (background)
 */

const P = 0x6fcbff; // primary
const A = 0x3a8b9f; // accent
const L = 0xa4c9df; // light

// ── Orbital particle trail ─────────────────────────────────────────────────────
function OrbitalTrail() {
	const ref = useRef<THREE.Points>(null);

	const { pos, col } = useMemo(() => {
		const count = 640;
		const pos = new Float32Array(count * 3);
		const col = new Float32Array(count * 3);
		for (let i = 0; i < count; i++) {
			const a = (i / count) * Math.PI * 2;
			const r = 2.4 + (Math.random() - 0.5) * 0.18;
			pos[i * 3] = Math.cos(a) * r;
			pos[i * 3 + 1] = (Math.random() - 0.5) * 0.035;
			pos[i * 3 + 2] = Math.sin(a) * r;
			// gradient along ring: cyan → teal
			const t = (i / count) * Math.PI * 2;
			const c = new THREE.Color(P).lerp(
				new THREE.Color(A),
				(Math.sin(t) + 1) / 2,
			);
			col[i * 3] = c.r;
			col[i * 3 + 1] = c.g;
			col[i * 3 + 2] = c.b;
		}
		return { pos, col };
	}, []);

	useFrame(({ clock }) => {
		if (!ref.current) return;
		const t = clock.elapsedTime;
		ref.current.rotation.y = t * 0.07;
		ref.current.rotation.x = Math.PI * 0.12 + Math.sin(t * 0.08) * 0.015;
	});

	return (
		<points ref={ref}>
			<bufferGeometry>
				<bufferAttribute attach="attributes-position" args={[pos, 3]} />
				<bufferAttribute attach="attributes-color" args={[col, 3]} />
			</bufferGeometry>
			<pointsMaterial
				size={0.028}
				vertexColors
				transparent
				opacity={0.65}
				blending={THREE.AdditiveBlending}
				depthWrite={false}
				sizeAttenuation
			/>
		</points>
	);
}

// ── Secondary scattered particles (inner halo) ─────────────────────────────────
function ParticleHalo() {
	const ref = useRef<THREE.Points>(null);

	const { pos, col } = useMemo(() => {
		const count = 320;
		const pos = new Float32Array(count * 3);
		const col = new Float32Array(count * 3);
		for (let i = 0; i < count; i++) {
			const theta = Math.random() * Math.PI * 2;
			const phi = Math.acos(2 * Math.random() - 1);
			const r = 1.8 + Math.random() * 0.6;
			pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
			pos[i * 3 + 1] = r * Math.cos(phi);
			pos[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
			const bright = 0.4 + Math.random() * 0.6;
			col[i * 3] = 0.43 * bright;
			col[i * 3 + 1] = 0.78 * bright;
			col[i * 3 + 2] = bright;
		}
		return { pos, col };
	}, []);

	useFrame(({ clock }) => {
		if (!ref.current) return;
		const t = clock.elapsedTime;
		ref.current.rotation.y = t * 0.012;
		ref.current.rotation.x = Math.sin(t * 0.03) * 0.06;
	});

	return (
		<points ref={ref}>
			<bufferGeometry>
				<bufferAttribute attach="attributes-position" args={[pos, 3]} />
				<bufferAttribute attach="attributes-color" args={[col, 3]} />
			</bufferGeometry>
			<pointsMaterial
				size={0.015}
				vertexColors
				transparent
				opacity={0.35}
				blending={THREE.AdditiveBlending}
				depthWrite={false}
				sizeAttenuation
			/>
		</points>
	);
}

// ── Glass sphere (hero) ────────────────────────────────────────────────────────
function GlassShell() {
	const ref = useRef<THREE.Mesh>(null);
	const cursor = useMemo(() => new THREE.Vector2(), []);

	useFrame(({ pointer, clock }) => {
		if (!ref.current) return;
		const t = clock.elapsedTime;
		cursor.lerp(pointer, 0.045);
		ref.current.rotation.y = t * 0.03 + cursor.x * 0.18;
		ref.current.rotation.x = t * 0.015 + cursor.y * 0.1;
		// micro-breath scale
		ref.current.scale.setScalar(1 + Math.sin(t * 0.7) * 0.008);
	});

	return (
		<mesh ref={ref}>
			<icosahedronGeometry args={[2.18, 14]} />
			<meshPhysicalMaterial
				color={P}
				metalness={0.04}
				roughness={0.02}
				transmission={0.92}
				thickness={1.8}
				transparent
				opacity={0.2}
				clearcoat={1}
				clearcoatRoughness={0.015}
				ior={1.52}
				envMapIntensity={0.8}
				reflectivity={0.3}
			/>
		</mesh>
	);
}

// ── Inner ember ────────────────────────────────────────────────────────────────
function InnerEmber() {
	const ref = useRef<THREE.Mesh>(null);

	useFrame(({ clock }) => {
		if (!ref.current) return;
		const t = clock.elapsedTime;
		const pulse = 0.92 + Math.sin(t * 1.2) * 0.08;
		ref.current.scale.setScalar(pulse);
		const mat = ref.current.material as THREE.MeshBasicMaterial;
		mat.opacity = 0.08 + Math.sin(t * 1.2) * 0.04;
	});

	return (
		<mesh ref={ref}>
			<dodecahedronGeometry args={[0.35, 0]} />
			<meshBasicMaterial color={L} transparent opacity={0.08} />
		</mesh>
	);
}

// ── Dynamic lights ─────────────────────────────────────────────────────────────
function SceneLights({ ambientColor }: { ambientColor: number }) {
	const key = useRef<THREE.PointLight>(null);
	const fill = useRef<THREE.PointLight>(null);

	useFrame(({ clock }) => {
		if (!key.current || !fill.current) return;
		const t = clock.elapsedTime;
		key.current.position.x = Math.sin(t * 0.35) * 5.5;
		key.current.position.z = Math.cos(t * 0.3) * 5.5;
		fill.current.position.x = Math.cos(t * 0.22) * -4.5;
		fill.current.position.z = Math.sin(t * 0.28) * 4.5;
	});

	return (
		<>
			<pointLight
				ref={key}
				color={P}
				intensity={38}
				distance={32}
				position={[4, 3, 5]}
			/>
			<pointLight
				ref={fill}
				color={A}
				intensity={16}
				distance={22}
				position={[-4, -2.5, 3]}
			/>
			<pointLight
				color={L}
				intensity={10}
				distance={16}
				position={[0, 4, -6]}
			/>
			<ambientLight color={ambientColor} intensity={3} />
		</>
	);
}

// ── Exported component ─────────────────────────────────────────────────────────
type GlassSphereSceneProps = React.ComponentProps<'div'>;

type CanvasErrorBoundaryProps = {
	fallback: React.ReactNode;
	children: React.ReactNode;
};

type CanvasErrorBoundaryState = { failed: boolean };

class CanvasErrorBoundary extends Component<
	CanvasErrorBoundaryProps,
	CanvasErrorBoundaryState
> {
	state: CanvasErrorBoundaryState = { failed: false };

	static getDerivedStateFromError(): CanvasErrorBoundaryState {
		return { failed: true };
	}

	render() {
		if (this.state.failed) return this.props.fallback;
		return this.props.children;
	}
}

function GlassSphereFallback({ clearColor }: { clearColor: number }) {
	const base = `#${clearColor.toString(16).padStart(6, '0')}`;
	return (
		<div
			className="h-full w-full"
			style={{
				background: `radial-gradient(55% 50% at 50% 45%, #6fcbff2e 0%, #3a8b9f1f 45%, ${base} 100%)`,
			}}
		/>
	);
}

export function GlassSphereScene({
	className,
	...props
}: GlassSphereSceneProps) {
	const webgl = useWebGLAvailable();
	const [contextLost, setContextLost] = useState(false);
	const resolvedTheme = useResolvedTheme();
	const isLight = isLightTheme(resolvedTheme);
	const { clear: clearColor, ambient: ambientColor } = getGlassSphereColors(
		resolvedTheme,
		isLight,
	);

	const showCanvas = webgl && !contextLost;
	const fallback = <GlassSphereFallback clearColor={clearColor} />;

	return (
		<div
			className={cn('fixed inset-0 z-0', className)}
			aria-hidden="true"
			{...props}
		>
			{showCanvas ? (
				<CanvasErrorBoundary fallback={fallback}>
					<Canvas
						gl={{
							antialias: true,
							alpha: false,
							stencil: false,
							powerPreference: 'high-performance',
							failIfMajorPerformanceCaveat: false,
							toneMapping: THREE.ACESFilmicToneMapping,
							toneMappingExposure: 1.15,
						}}
						camera={{ position: [0, 0, 9], fov: 50, near: 0.1, far: 300 }}
						dpr={[1, 1.75]}
						onCreated={(state) => {
							state.gl.setClearColor(clearColor, 1);
							state.gl.domElement.addEventListener(
								'webglcontextlost',
								(event) => {
									event.preventDefault();
									setContextLost(true);
								},
								{ once: true },
							);
						}}
					>
						<GlassShell />
						<InnerEmber />
						<OrbitalTrail />
						<ParticleHalo />
						<SceneLights ambientColor={ambientColor} />
					</Canvas>
				</CanvasErrorBoundary>
			) : (
				fallback
			)}
		</div>
	);
}
